// Set up all fields and multiple validators for each
import React from "react";
import { useField } from "formik";
import * as yup from "yup";
import "data/yup-validation";
import { Checkbox, FormControl, FormControlLabel, FormHelperText, Grid } from "@mui/material";
import { keyFromIdentity } from "../helpers";
import { QuestionProps } from "../question";
import OutlinedDiv from "./outlined";
import DateInput from "./date";

const initial = (
  question: QuestionProps,
  _questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
): Record<string, Record<string, null>> => {
  const key = keyFromIdentity(question.identity, parent, index);
  return {
    [key]: {
      begin: null,
      end: null,
      current: null,
    },
  };
};

const validator = (
  question: QuestionProps,
  _questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
): Record<string, yup.AnySchema> => {
  const key = keyFromIdentity(question.identity, parent, index);
  const todayEnd = new Date();
  const errorMessage = "End date is required unless it's current";
  todayEnd.setUTCHours(23, 59, 59, 999);
  const validateBegin = yup.date()
    .typeError("Invalid date format")
    .max(todayEnd, "Should not be in the future")
    .when(
      ["current", "end"],
      ([currentVal, endVal], schema) => {
        const current = Array.isArray(currentVal) ? currentVal[0] : currentVal;
        const end = Array.isArray(endVal) ? endVal[0] : endVal;
        const withCompare =
          !current && end ? schema.max(end, "Start date should be before the end date") : schema;
        if (question.required || current || end) {
          return withCompare.required().requireOneOfFields(["end", "current"], errorMessage);
        }
        return withCompare.nullable();
      },
    );

  return {
    [key]: yup.object().shape({
      begin: validateBegin,
      end: yup.date()
        .nullable()
        .typeError("Invalid date format"),
      current: yup.boolean()
        .nullable(),
    }),
  };
};

const DateRangeInput = (props: any) => {
  const { name: parentName } = props;
  const begin = `${parentName}.begin`;
  const end = `${parentName}.end`;
  const current = `${parentName}.current`;
  const [isCurrent, meta] = useField(current);
  const errorClass = meta.touched && !!meta.error ? "color-hc-error" : "";
  return (<OutlinedDiv label={props.title} fullWidth required={props.required}>
    <Grid container spacing={1}>
      <Grid size={{ xs: 12, sm: 12 }}>
        <DateInput {...props} name={begin} title="Start" disableFuture />
      </Grid>
      <Grid size={{ xs: 12, sm: 12 }}>
        <DateInput {...props} name={end} title="End" disabled={!!isCurrent.value} disableFuture />
      </Grid>

      {props.data.allowPresent && <Grid size={{ xs: 12 }}>
        <FormControl>
          <FormControlLabel
            {...isCurrent}
            control={<Checkbox className={errorClass} checked={isCurrent.value} />}
            label={props.data.label || "Currently"}
            className={errorClass}
          />
          {meta.touched && meta.error && (
            <FormHelperText error>
              {meta.error}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>}
    </Grid>
  </OutlinedDiv>);
};

DateRangeInput.validator = validator;
DateRangeInput.initial = initial;

export default DateRangeInput;
